/**
 * The currency the tax administration certifies sales invoices in.
 *
 * It is a dated parameter per jurisdiction: a list of periods, each naming
 * a currency from its `effectiveFrom` day on, stored in the platform table
 * `CertificationCurrencyPeriod`. Every guard on a certifying path resolves
 * the expected currency through `certificationCurrencyAt` and refuses any
 * other with `assertCertificationCurrency`; none compares with a literal.
 *
 * Days are UTC calendar days: a period starts at 00:00 UTC on its
 * `effectiveFrom`. The registry admits only jurisdictions at UTC+0 all year
 * (jurisdictions/registry.ts), where the UTC day is the local day.
 */
import { InvoiceError } from "../errors/index.js";
import { JURISDICTIONS } from "../jurisdictions/registry.js";
import { CURRENCY_LABELS, CURRENCY_MINOR_UNITS, centimesPerMinorUnit, } from "./money.js";
/**
 * The first period of Burkina Faso, from its registry entry, seeded by
 * `packages/api/prisma/sql/currency-period-guards.sql` in every database and
 * by the mcf-agent in its own store. A test keeps the two identical.
 */
export const CERTIFICATION_CURRENCY_ORIGIN = Object.freeze({
    id: "certification-currency-bf-origin",
    jurisdiction: JURISDICTIONS["BF"].code,
    currency: JURISDICTIONS["BF"].currencyOrigin.currency,
    effectiveFrom: "1900-01-01",
    legalReference: JURISDICTIONS["BF"].currencyOrigin.legalReference,
    createdById: "system",
});
/** ISO 4217 alphabetic code: three upper-case letters. */
export const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
/** The French refusal of a malformed currency code. */
export function invalidCurrencyCodeMessage(value) {
    return `Code devise invalide : « ${String(value)} » (code ISO 4217 en trois lettres majuscules attendu)`;
}
/** True for three upper-case letters. */
export function isCurrencyCode(value) {
    return typeof value === "string" && CURRENCY_CODE_PATTERN.test(value);
}
/** `value` as a currency code; refuses anything but three upper-case letters. */
export function parseCurrencyCode(value) {
    if (!isCurrencyCode(value)) {
        throw new InvoiceError(invalidCurrencyCodeMessage(value), "DEVISE_INVALIDE", 400);
    }
    return value;
}
/** The `YYYY-MM-DD` day of a date or of a stored day. */
export function currencyDay(value) {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
        return value;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new InvoiceError(`Date invalide pour résoudre une monnaie : « ${String(value)} »`, "DATE_MONNAIE_INVALIDE", 400);
    }
    return date.toISOString().slice(0, 10);
}
/**
 * The currency of the latest active period starting on or before the day of
 * `date`, among `periods`. Throws when none covers the day or when two
 * active periods start the same day: nothing is certified on a guess.
 */
export function currencyOfPeriodsAt(periods, date) {
    const day = currencyDay(date);
    let best = null;
    let tie = false;
    for (const period of periods) {
        if (period.withdrawnAt != null)
            continue;
        const start = currencyDay(period.effectiveFrom);
        if (start > day)
            continue;
        if (best === null || start > best.day) {
            best = { day: start, currency: period.currency };
            tie = false;
        }
        else if (start === best.day) {
            tie = true;
        }
    }
    if (tie && best !== null) {
        throw new InvoiceError(`Deux périodes de monnaie actives commencent le ${best.day}`, "PERIODE_MONNAIE_AMBIGUE", 500);
    }
    return best?.currency ?? null;
}
/**
 * The certification currency of `jurisdiction` on the day of `date`.
 * Fails closed: a day no active period covers throws, it never falls back.
 */
export function certificationCurrencyAt(periods, jurisdiction, date) {
    const currency = currencyOfPeriodsAt(periods.filter((period) => period.jurisdiction === jurisdiction), date);
    if (currency === null) {
        throw new InvoiceError(`Aucune monnaie de certification n'est définie pour le ${currencyDay(date)} (${jurisdiction})`, "MONNAIE_CERTIFICATION_INDEFINIE", 500);
    }
    return currency;
}
function unknownCurrencyError(code) {
    return new InvoiceError(`La monnaie de certification ${code} n'est pas connue du logiciel : ses unités de paiement ne sont pas définies. Déclarez-les sur la période de la monnaie avant de l'appliquer.`, "MONNAIE_CERTIFICATION_INCONNUE", 500);
}
/**
 * `currency` when its payable unit is known: a definition that states one,
 * or a code of CURRENCY_MINOR_UNITS. A certification currency without one
 * could be certified but never settled: every payment, rounding and net to
 * pay needs its minor units.
 */
export function assertSettleableCurrency(currency) {
    if (typeof currency === "string") {
        if (CURRENCY_MINOR_UNITS[currency] === undefined)
            throw unknownCurrencyError(currency);
        return currency;
    }
    centimesPerMinorUnit(currency);
    return currency;
}
function hasWording(fields) {
    return [
        fields.symbol,
        fields.nameSingular,
        fields.namePlural,
        fields.nameFeminine,
        fields.hundredthSingular,
        fields.hundredthPlural,
    ].some((value) => value !== null && value !== undefined);
}
function nonBlank(value) {
    return typeof value === "string" && value.trim().length > 0;
}
/**
 * Why the unit and wording a period states for `code` cannot be used, or
 * null. A code the tables do not know needs both; a stated wording names
 * the symbol and both forms of the name, and the hundredth whenever the
 * currency has minor units (the amount in words states them).
 */
export function currencyDefinitionFieldsError(code, fields) {
    const digits = fields.minorUnits ?? CURRENCY_MINOR_UNITS[code];
    if (digits === undefined) {
        return `La monnaie ${code} n'est pas connue du logiciel : indiquez son nombre de décimales (0, 1 ou 2)`;
    }
    if (!Number.isInteger(digits) || digits < 0 || digits > 2) {
        return `Nombre de décimales invalide pour ${code} : ${String(digits)} (0, 1 ou 2 attendu)`;
    }
    if (!hasWording(fields)) {
        return CURRENCY_LABELS[code] === undefined
            ? `La monnaie ${code} n'est pas connue du logiciel : indiquez son symbole et son nom au singulier et au pluriel`
            : null;
    }
    if (!nonBlank(fields.symbol) || !nonBlank(fields.nameSingular) || !nonBlank(fields.namePlural)) {
        return `Le libellé de la monnaie ${code} doit comporter le symbole et le nom au singulier et au pluriel`;
    }
    const hundredth = [fields.hundredthSingular, fields.hundredthPlural];
    if (hundredth.some(nonBlank) && !hundredth.every(nonBlank)) {
        return `Le nom de la subdivision de ${code} doit être donné au singulier et au pluriel`;
    }
    if (digits > 0 && !hundredth.every(nonBlank)) {
        return `La monnaie ${code} a ${digits} décimale${digits > 1 ? "s" : ""} : indiquez le nom de sa subdivision au singulier et au pluriel (montant en lettres)`;
    }
    return null;
}
/** The wording a period states, or undefined when it states none. */
function periodLabel(fields) {
    if (!hasWording(fields))
        return undefined;
    return {
        symbol: fields.symbol,
        singular: fields.nameSingular,
        plural: fields.namePlural,
        feminine: fields.nameFeminine ?? false,
        hundredth: nonBlank(fields.hundredthSingular) && nonBlank(fields.hundredthPlural)
            ? { singular: fields.hundredthSingular, plural: fields.hundredthPlural }
            : null,
    };
}
/**
 * The currency `code` as the money functions take it (#139): the definition
 * the certification-currency periods naming it state (unit, wording), or the
 * bare code when none states one and the code tables know it.
 *
 * Withdrawn periods count: an invoice certified in a currency whose switch
 * was withdrawn later is still settled and printed in it. Periods of one
 * code that state different definitions, or a code with neither a stated
 * unit nor a known one, refuse: no amount is settled on a guess.
 */
export function currencyDefinitionOf(periods, code) {
    const definitions = new Map();
    for (const period of periods) {
        if (period.currency !== code)
            continue;
        if ((period.minorUnits === null || period.minorUnits === undefined) && !hasWording(period))
            continue;
        const error = currencyDefinitionFieldsError(code, period);
        if (error !== null) {
            throw new InvoiceError(error, "MONNAIE_CERTIFICATION_INCONNUE", 500);
        }
        const label = periodLabel(period);
        const definition = {
            code,
            minorUnits: period.minorUnits ?? CURRENCY_MINOR_UNITS[code],
            ...(label !== undefined ? { label } : {}),
        };
        definitions.set(JSON.stringify(definition), definition);
    }
    if (definitions.size > 1) {
        throw new InvoiceError(`Les périodes de la monnaie ${code} ne définissent pas la même unité de paiement ni le même libellé`, "PERIODE_MONNAIE_AMBIGUE", 500);
    }
    const [definition] = definitions.values();
    if (definition !== undefined)
        return definition;
    return assertSettleableCurrency(code);
}
/**
 * The working (functional) currency of a tenant on the day of `date` (#140):
 * its latest active period on or before the day, else the certification
 * currency of that day (AUDCIF art. 17: the books are kept in legal tender).
 */
export function workingCurrencyAt(tenantPeriods, certificationPeriods, jurisdiction, date) {
    return (currencyOfPeriodsAt(tenantPeriods, date) ??
        certificationCurrencyAt(certificationPeriods, jurisdiction, date));
}
function previousDay(day) {
    return new Date(Date.parse(`${day}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
}
/**
 * The days on which the working currency of a tenant changes, in order:
 * each day some active period (the tenant's own or the jurisdiction's
 * certification periods) starts and on which the currency differs from the
 * day before. The first period of the history is no change.
 */
export function workingCurrencyChanges(tenantPeriods, certificationPeriods, jurisdiction) {
    const starts = [
        ...tenantPeriods,
        ...certificationPeriods.filter((period) => period.jurisdiction === jurisdiction),
    ]
        .filter((period) => period.withdrawnAt == null)
        .map((period) => currencyDay(period.effectiveFrom));
    const changes = [];
    for (const day of [...new Set(starts)].sort()) {
        let from;
        try {
            from = workingCurrencyAt(tenantPeriods, certificationPeriods, jurisdiction, previousDay(day));
        }
        catch {
            continue;
        }
        const to = workingCurrencyAt(tenantPeriods, certificationPeriods, jurisdiction, day);
        if (to !== from)
            changes.push({ day, from, to });
    }
    return changes;
}
/**
 * The first day, on or after `fromDay`, on which the working currency of a
 * tenant differs from the certification currency, with both currencies;
 * null when they agree on every day. Only the days a period starts need
 * checking: both currencies are constant in between.
 */
export function firstWorkingCurrencyDivergence(tenantPeriods, certificationPeriods, jurisdiction, fromDay) {
    const starts = [...tenantPeriods, ...certificationPeriods.filter((p) => p.jurisdiction === jurisdiction)]
        .filter((period) => period.withdrawnAt == null)
        .map((period) => currencyDay(period.effectiveFrom))
        .filter((day) => day >= fromDay);
    for (const day of [...new Set([fromDay, ...starts])].sort()) {
        const working = workingCurrencyAt(tenantPeriods, certificationPeriods, jurisdiction, day);
        const certified = certificationCurrencyAt(certificationPeriods, jurisdiction, day);
        if (working !== certified)
            return { day, working, certified };
    }
    return null;
}
/**
 * Why a new working-currency period may not be written, or null (#140).
 * No jurisdiction lets a working currency differ from the certification
 * currency, so the rule is strict equality on every day from the period's
 * start; a period naming the currency the tenant would inherit anyway is
 * redundant.
 */
export function tenantCurrencyPeriodError(candidate, tenantPeriods, certificationPeriods, jurisdiction) {
    const day = currencyDay(candidate.effectiveFrom);
    if (workingCurrencyAt(tenantPeriods, certificationPeriods, jurisdiction, day) === candidate.currency) {
        return `La période de monnaie de travail en ${candidate.currency} à partir du ${day} est redondante : l'entreprise tient déjà ses comptes dans cette monnaie à cette date`;
    }
    const divergence = firstWorkingCurrencyDivergence([...tenantPeriods, candidate], certificationPeriods, jurisdiction, day);
    return divergence === null
        ? null
        : `La monnaie de travail (${divergence.working}) différerait de la monnaie de certification (${divergence.certified}) à partir du ${divergence.day}`;
}
/**
 * The French rule a currency refusal states. Every invoice is issued in the
 * certification currency, but an ORDINARY one carries no certification
 * mention, the refusal included.
 */
export function certificationCurrencyRule(expected, regime) {
    return regime === "ORDINARY"
        ? `Les factures doivent être émises en ${expected}`
        : `Les factures certifiées doivent être en ${expected}`;
}
/** Refuses a currency other than the certification currency `expected`. */
export function assertCertificationCurrency(currency, expected, regime = "CERTIFIED") {
    if (currency !== expected) {
        throw new InvoiceError(certificationCurrencyRule(expected, regime), "DEVISE_NON_CERTIFIABLE", 400);
    }
}
//# sourceMappingURL=certification-currency.js.map